/**
 * #4215: ONE throw-safe wrapper around `window.matchMedia`.
 *
 * `matchMedia` can throw (SecurityError/TypeError in sandboxed iframes and some
 * embedded webviews), can be absent (no-jsdom renders), and has been seen to
 * return `null`. Each of those, hit in a `useState` initialiser, a mount
 * effect or module scope, unmounts the page (or fails the module import).
 * Before this helper every call site re-implemented the guard and most checked
 * only `typeof window.matchMedia === 'function'`, which catches the absent
 * case but not the throw or the null return. See also `isTouchDevice.js`
 * (#2217), which carries its own copy of the same try/catch. (ScenariosPageWorking's
 * `useCoarsePointer`, #3483, used to carry one too; it uses this helper since #4215.)
 *
 * `window.matchMedia` is read at CALL time, never cached at import, so a test
 * (or a late polyfill) that stubs it after import is honoured.
 */

/**
 * @param {string} query
 * @returns {MediaQueryList | null} the list, or null when `window` is
 *   undefined, `matchMedia` is not a function, it throws, or it returns a
 *   falsy value.
 */
export function safeMatchMedia(query) {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  try {
    return window.matchMedia(query) || null;
  } catch {
    return null;
  }
}

/**
 * @param {string} query
 * @param {boolean} [fallback=false] returned when no list is available. Pass
 *   the value the call site had before this helper existed.
 * @returns {boolean}
 */
export function mediaQueryMatches(query, fallback = false) {
  const mql = safeMatchMedia(query);
  if (!mql) return fallback;
  return Boolean(mql.matches);
}

/**
 * Subscribe to a list's `change` event. Uses `addEventListener`, falls back to
 * the deprecated `addListener` (Safari < 14), and never throws.
 *
 * @param {MediaQueryList | null} mql
 * @param {(event: MediaQueryListEvent) => void} onChange
 * @returns {() => void} unsubscribe; a no-op when there is nothing to remove.
 */
export function subscribeMediaQuery(mql, onChange) {
  if (!mql) return () => {};
  try {
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => {
        try { mql.removeEventListener('change', onChange); } catch { /* ignore */ }
      };
    }
    if (typeof mql.addListener === 'function') {
      mql.addListener(onChange);
      return () => {
        try { mql.removeListener?.(onChange); } catch { /* ignore */ }
      };
    }
  } catch {
    // A throwing subscribe leaves the value static: same as no listener.
  }
  return () => {};
}
